/**
 * Grid placement validator (ADR-0015 §1, §16.1, amended §18).
 *
 * Pure geometry over a single grid — no card semantics, no combat state. Bounds
 * are passed explicitly (no module-level grid-size constant per §18) so the
 * player loadout (3×3) and the tier-scaled enemy loadouts (§4) are each
 * validated against their own capacity. V1 grids are flat: no blocked,
 * coloured, or decorative cells (ADR-0015 §13), so the only constraints are
 * in-bounds and no-overlap.
 */

import type { Card, GridPlacement } from './types';

type Origin = { row: number; col: number };
type Bounds = { rows: number; cols: number };
type Footprint = { rows: number; cols: number };

/** A card's occupied footprint, accounting for committed rotation: a rotated
 * card swaps its width and height (ADR-0015 §9). `size.w` is columns, `size.h`
 * is rows. */
export function footprint(card: Card, rotated: boolean): Footprint {
  return rotated
    ? { rows: card.size.w, cols: card.size.h }
    : { rows: card.size.h, cols: card.size.w };
}

function occupies(origin: Origin, fp: Footprint, row: number, col: number): boolean {
  return (
    row >= origin.row &&
    row < origin.row + fp.rows &&
    col >= origin.col &&
    col < origin.col + fp.cols
  );
}

function inBounds(origin: Origin, fp: Footprint, bounds: Bounds): boolean {
  return (
    origin.row >= 0 &&
    origin.col >= 0 &&
    origin.row + fp.rows <= bounds.rows &&
    origin.col + fp.cols <= bounds.cols
  );
}

/**
 * Whether `card` can be placed at `origin` (top-left cell) on the grid described
 * by `placements` and `bounds`, in the given rotation. True iff the footprint
 * stays in-bounds of `bounds` and overlaps no existing placement.
 *
 * Bounds are explicit per §18: pass the loadout's capacity + placements to
 * validate a loadout move; the player and enemy loadouts size independently.
 */
export function canPlace(
  placements: readonly GridPlacement[],
  bounds: Bounds,
  card: Card,
  origin: Origin,
  rotated: boolean,
): boolean {
  const fp = footprint(card, rotated);
  if (!inBounds(origin, fp, bounds)) return false;

  for (const placed of placements) {
    const placedFp = footprint(placed.card, placed.rotated);
    for (let r = origin.row; r < origin.row + fp.rows; r++) {
      for (let c = origin.col; c < origin.col + fp.cols; c++) {
        if (occupies(placed.origin, placedFp, r, c)) return false;
      }
    }
  }
  return true;
}
